/**
 * Seam geometry — the one place that knows a stitch seam can CURVE (#982,
 * epic #781).
 *
 * Seams were polyline-by-design: `StitchLine.points` is a flat `Vec2[]`, and
 * every consumer — the hole walker, the crop, the guide rendering, the
 * exporters — assumed straight runs between them. A seam could only approximate
 * a curve with many points, and a seam traced from a curved construction edge
 * had to SAMPLE that curve into points rather than keep it.
 *
 * A seam now carries optional bezier handles alongside its points, the same
 * geometry construction lines and piece outlines have had all along.
 *
 * HANDLES ARE RELATIVE to their anchor, exactly as paper.js stores them. That
 * choice does real work: a translation leaves them untouched, a rotation turns
 * the vectors without needing the centre, and a resize scales them — so the
 * transform helpers can't accidentally leave a curve's handles behind in the old
 * frame while its anchors move. Absent handles (every seam ever saved before
 * this) mean straight, so old projects load byte-identical.
 *
 * Everything downstream works in ARC LENGTH, which is what makes curves
 * tractable: `buildSeamSegments` flattens each curved run into a cumulative
 * length table once, and `seamPlacementAt` answers "where am I at distance d,
 * and which way am I pointing" from it. The hole walker never learns what a
 * bezier is.
 */
import type paper from "paper";
import type { StitchLine, Vec2 } from "./Project";

/** A bezier handle pair for one anchor, RELATIVE to that anchor's position. */
export interface SeamHandle {
  in: Vec2;
  out: Vec2;
}

/** Samples per curved run when flattening to an arc-length table. A seam is
 *  centimetres long and its holes are millimetres apart, so 64 puts the
 *  parameterisation error orders of magnitude below anything punchable. */
const LUT_SAMPLES = 64;

/** Below this a handle is no handle — it keeps a straight run straight rather
 *  than emitting a cubic that happens to be a line. */
const HANDLE_EPS = 1e-9;

function isZero(v: Vec2 | undefined): boolean {
  return !v || (Math.abs(v.x) < HANDLE_EPS && Math.abs(v.y) < HANDLE_EPS);
}

/** True when any run of this seam is actually curved. Lets every consumer keep
 *  its fast polyline path for the overwhelmingly common straight seam. */
export function seamHasCurves(line: {
  points: Vec2[];
  handles?: SeamHandle[];
  closed?: boolean;
}): boolean {
  const h = line.handles;
  if (!h || h.length === 0) return false;
  return h.some((e) => !isZero(e?.in) || !isZero(e?.out));
}

/** The handle pair for anchor `i`, or a zero pair. */
function handleAt(handles: SeamHandle[] | undefined, i: number): SeamHandle {
  const e = handles?.[i];
  return {
    in: e?.in ?? { x: 0, y: 0 },
    out: e?.out ?? { x: 0, y: 0 },
  };
}

/**
 * The seam as SVG path data, curves included.
 *
 * The single source of truth for "what shape is this seam" — shared by the
 * renderer, the exporters, hit-testing and the knife, so none of them can drift
 * into drawing a curved seam as a polyline.
 */
export function seamPathData(line: {
  points: Vec2[];
  handles?: SeamHandle[];
  closed?: boolean;
}): string {
  const pts = line.points;
  if (pts.length < 2) return "";
  const parts: string[] = [`M ${pts[0]!.x} ${pts[0]!.y}`];
  const run = runEmitter(pts, line.handles);
  for (let i = 0; i + 1 < pts.length; i++) parts.push(run(i, i + 1));
  if (line.closed) {
    parts.push(run(pts.length - 1, 0));
    parts.push("Z");
  }
  return parts.join(" ");
}

/** The SVG command for the run from anchor `i` to anchor `j` — a line when
 *  neither facing handle bends it, a cubic otherwise. */
function runEmitter(
  pts: Vec2[],
  handles: SeamHandle[] | undefined,
): (i: number, j: number) => string {
  return (i, j) => {
    const a = pts[i]!;
    const b = pts[j]!;
    const ha = handleAt(handles, i);
    const hb = handleAt(handles, j);
    if (isZero(ha.out) && isZero(hb.in)) return `L ${b.x} ${b.y}`;
    return `C ${a.x + ha.out.x} ${a.y + ha.out.y} ${b.x + hb.in.x} ${b.y + hb.in.y} ${b.x} ${b.y}`;
  };
}

/**
 * A closed seam walked `laps` times as one OPEN path, ending back at the first
 * anchor.
 *
 * The working-area overlay for a windowed loop needs this: a window can wrap
 * past the start-of-path origin, and walking two laps turns that wrap into a
 * single continuous slice that `splitAt` can trim, instead of two disjoint arcs
 * to stitch back together.
 */
export function seamPathDataLaps(
  line: { points: Vec2[]; handles?: SeamHandle[] },
  laps: number,
): string {
  const pts = line.points;
  if (pts.length < 2 || laps < 1) return "";
  const parts: string[] = [`M ${pts[0]!.x} ${pts[0]!.y}`];
  const run = runEmitter(pts, line.handles);
  for (let lap = 0; lap < laps; lap++) {
    for (let i = 0; i + 1 < pts.length; i++) parts.push(run(i, i + 1));
    parts.push(run(pts.length - 1, 0));
  }
  return parts.join(" ");
}

/** How many runs a seam has: one per pair of adjacent anchors, plus the
 *  closing run on a loop. Run `i` goes from anchor `i` to the anchor after it. */
export function seamRunCount(line: {
  points: Vec2[];
  closed?: boolean;
}): number {
  const n = line.points.length;
  if (n < 2) return 0;
  return line.closed ? n : n - 1;
}

/** ONE run of a seam as its own path data — run `i`, from anchor `i` to the
 *  next (wrapping on a closed seam). Curved when either facing handle bends it,
 *  exactly as the whole seam would draw it, which is what lets an overlay
 *  highlight one stretch of a seam without re-deriving the rest. */
export function seamRunPathData(
  line: { points: Vec2[]; handles?: SeamHandle[]; closed?: boolean },
  index: number,
): string {
  const pts = line.points;
  if (index < 0 || index >= seamRunCount(line)) return "";
  const j = (index + 1) % pts.length;
  const a = pts[index]!;
  return `M ${a.x} ${a.y} ${runEmitter(pts, line.handles)(index, j)}`;
}

/** The seam as a Paper path, built from its anchors and handles rather than
 *  parsed back out of path data.
 *
 *  Worth the few extra lines over `new Path({pathData: seamPathData(line)})`:
 *  the parse route has to close a ring by matching a final point against the
 *  first, so a caller can't be sure whether it gets `n` segments or `n + 1` —
 *  and anything indexing by run (hit-testing one stretch, say) needs the
 *  segment and curve numbering to line up with the seam's own. */
export function seamPaperPath(
  line: { points: Vec2[]; handles?: SeamHandle[]; closed?: boolean },
  scope: paper.PaperScope,
): paper.Path {
  const path = new scope.Path({ insert: false });
  line.points.forEach((p, i) => {
    const h = handleAt(line.handles, i);
    path.add(
      new scope.Segment(
        new scope.Point(p.x, p.y),
        new scope.Point(h.in.x, h.in.y),
        new scope.Point(h.out.x, h.out.y),
      ),
    );
  });
  path.closed = line.closed === true;
  return path;
}

/** One run between two anchors, with its arc length. A curved run carries its
 *  control points and a cumulative-length table; a straight one needs neither. */
export interface SeamSegment {
  from: Vec2;
  to: Vec2;
  len: number;
  cumStart: number;
  /** Absolute cubic control points — present only on a curved run. */
  c1?: Vec2;
  c2?: Vec2;
  /** Cumulative arc length at t = i / LUT_SAMPLES. Present with `c1` / `c2`. */
  lut?: number[];
}

function bezierPoint(p0: Vec2, c1: Vec2, c2: Vec2, p3: Vec2, t: number): Vec2 {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * c1.x + c * c2.x + d * p3.x,
    y: a * p0.y + b * c1.y + c * c2.y + d * p3.y,
  };
}

function bezierTangent(
  p0: Vec2,
  c1: Vec2,
  c2: Vec2,
  p3: Vec2,
  t: number,
): Vec2 {
  const u = 1 - t;
  const a = 3 * u * u;
  const b = 6 * u * t;
  const c = 3 * t * t;
  return {
    x: a * (c1.x - p0.x) + b * (c2.x - c1.x) + c * (p3.x - c2.x),
    y: a * (c1.y - p0.y) + b * (c2.y - c1.y) + c * (p3.y - c2.y),
  };
}

/**
 * Per-run arc-length table for a seam, plus its total length. A closed seam
 * gets the wrap run from the last anchor back to the first appended, so callers
 * walk all the way around.
 *
 * This replaces the straight-only table the hole samplers used to build. Every
 * caller above it works in arc length and is unchanged by curves.
 */
export function buildSeamSegments(
  points: Vec2[],
  closed: boolean,
  handles?: SeamHandle[],
): { segs: SeamSegment[]; total: number } {
  const segs: SeamSegment[] = [];
  let total = 0;
  const push = (i: number, j: number) => {
    const from = points[i]!;
    const to = points[j]!;
    const ha = handleAt(handles, i);
    const hb = handleAt(handles, j);
    if (isZero(ha.out) && isZero(hb.in)) {
      const len = Math.hypot(to.x - from.x, to.y - from.y);
      if (len < 1e-6) return;
      segs.push({ from, to, len, cumStart: total });
      total += len;
      return;
    }
    const c1 = { x: from.x + ha.out.x, y: from.y + ha.out.y };
    const c2 = { x: to.x + hb.in.x, y: to.y + hb.in.y };
    // Flatten once: cumulative chord length at each sample. Chord length
    // under-reads a curve, but at 64 samples the shortfall is far below the
    // sub-millimetre scale hole placement cares about.
    const lut: number[] = [0];
    let prev = from;
    for (let k = 1; k <= LUT_SAMPLES; k++) {
      const pt = bezierPoint(from, c1, c2, to, k / LUT_SAMPLES);
      lut.push(lut[k - 1]! + Math.hypot(pt.x - prev.x, pt.y - prev.y));
      prev = pt;
    }
    const len = lut[LUT_SAMPLES]!;
    if (len < 1e-6) return;
    segs.push({ from, to, len, cumStart: total, c1, c2, lut });
    total += len;
  };
  for (let i = 0; i + 1 < points.length; i++) push(i, i + 1);
  if (closed && points.length >= 2) push(points.length - 1, 0);
  return { segs, total };
}

/** The `t` on a curved run at local arc length `dl`, from its table. */
function tAtLength(seg: SeamSegment, dl: number): number {
  const lut = seg.lut!;
  const target = Math.max(0, Math.min(seg.len, dl));
  let lo = 0;
  let hi = LUT_SAMPLES;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (lut[mid]! <= target) lo = mid;
    else hi = mid;
  }
  const spanLen = lut[hi]! - lut[lo]!;
  const frac = spanLen < 1e-12 ? 0 : (target - lut[lo]!) / spanLen;
  return (lo + frac) / LUT_SAMPLES;
}

/**
 * Position + local direction at arc length `d` along the seam.
 *
 * The tangent is the curve's true derivative on a curved run, not the chord
 * between flattening samples — slanted and diamond chisel marks are ORIENTED by
 * it, so a stair-stepped tangent would be visible as marks that don't quite
 * follow the seam.
 */
export function seamPlacementAt(
  segs: SeamSegment[],
  d: number,
): { position: Vec2; tangentDeg: number } {
  let seg = segs[segs.length - 1]!;
  for (const s of segs) {
    if (d <= s.cumStart + s.len + 1e-9) {
      seg = s;
      break;
    }
  }
  if (!seg.lut) {
    const t = seg.len < 1e-9 ? 0 : (d - seg.cumStart) / seg.len;
    return {
      position: {
        x: seg.from.x + (seg.to.x - seg.from.x) * t,
        y: seg.from.y + (seg.to.y - seg.from.y) * t,
      },
      tangentDeg:
        (Math.atan2(seg.to.y - seg.from.y, seg.to.x - seg.from.x) * 180) /
        Math.PI,
    };
  }
  const t = tAtLength(seg, d - seg.cumStart);
  const position = bezierPoint(seg.from, seg.c1!, seg.c2!, seg.to, t);
  let tan = bezierTangent(seg.from, seg.c1!, seg.c2!, seg.to, t);
  // The derivative vanishes where a handle sits exactly on its anchor (a cusp);
  // fall back to the chord so a mark still gets an orientation rather than 0°.
  if (Math.hypot(tan.x, tan.y) < 1e-9) {
    tan = { x: seg.to.x - seg.from.x, y: seg.to.y - seg.from.y };
  }
  return {
    position,
    tangentDeg: (Math.atan2(tan.y, tan.x) * 180) / Math.PI,
  };
}

// ---------------------------------------------------------------------------
// Transforms
// ---------------------------------------------------------------------------

/** Rotate a seam's handles by `deg`. Relative vectors, so no centre is needed —
 *  which is the point of storing them relative. */
export function rotateSeamHandles(
  handles: SeamHandle[] | undefined,
  deg: number,
): SeamHandle[] | undefined {
  if (!handles) return undefined;
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const spin = (v: Vec2): Vec2 => ({
    x: v.x * cos - v.y * sin,
    y: v.x * sin + v.y * cos,
  });
  return handles.map((h) => ({ in: spin(h.in), out: spin(h.out) }));
}

/** Scale a seam's handles by independent axis factors. */
export function scaleSeamHandles(
  handles: SeamHandle[] | undefined,
  sx: number,
  sy: number,
): SeamHandle[] | undefined {
  if (!handles) return undefined;
  const s = (v: Vec2): Vec2 => ({ x: v.x * sx, y: v.y * sy });
  return handles.map((h) => ({ in: s(h.in), out: s(h.out) }));
}

/** Reflect a seam's handles across a horizontal or vertical axis. */
export function mirrorSeamHandles(
  handles: SeamHandle[] | undefined,
  axis: "horizontal" | "vertical",
): SeamHandle[] | undefined {
  return axis === "vertical"
    ? scaleSeamHandles(handles, 1, -1)
    : scaleSeamHandles(handles, -1, 1);
}

/**
 * Reverse a seam's handles to match a reversed point list.
 *
 * Two things flip, and missing either silently un-curves the seam: the ORDER of
 * the entries, and `in` / `out` within each entry — travelling the other way
 * makes what led into an anchor the thing that now leads out of it.
 */
export function reverseSeamHandles(
  handles: SeamHandle[] | undefined,
): SeamHandle[] | undefined {
  if (!handles) return undefined;
  return [...handles].reverse().map((h) => ({ in: h.out, out: h.in }));
}

/** Drop the handle list when nothing in it curves, so a straightened seam stops
 *  carrying an array of zeroes through every commit and serialisation. */
export function normalizeSeamHandles(
  handles: SeamHandle[] | undefined,
): SeamHandle[] | undefined {
  if (!handles) return undefined;
  return handles.some((h) => !isZero(h?.in) || !isZero(h?.out))
    ? handles
    : undefined;
}

/** A seam's handles, padded or trimmed to match `count` anchors. Edits that add
 *  or remove a point must keep the two lists index-aligned. */
export function fitSeamHandles(
  handles: SeamHandle[] | undefined,
  count: number,
): SeamHandle[] | undefined {
  if (!handles) return undefined;
  const out: SeamHandle[] = [];
  for (let i = 0; i < count; i++) out.push(handleAt(handles, i));
  return normalizeSeamHandles(out);
}

/**
 * Read an edited path back into a seam's anchors + handles.
 *
 * The inverse of {@link seamPathData}, and the reason the Edit-points tool can
 * work on a seam with the same machinery it uses on a part outline: the tool
 * hands back plain path data, and this turns it into the seam's own
 * representation. Paper stores handles relative to their segment already, so
 * they come across as-is.
 *
 * Returns null for anything that isn't a usable seam (unparseable, or fewer
 * than two anchors).
 */
export function seamFromPathData(
  pathData: string,
  scope: paper.PaperScope,
): { points: Vec2[]; handles?: SeamHandle[]; closed: boolean } | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subs =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  const sub = subs[0];
  if (!sub || !sub.segments || sub.segments.length < 2) {
    cp.remove();
    return null;
  }
  const points: Vec2[] = [];
  const handles: SeamHandle[] = [];
  for (const seg of sub.segments) {
    points.push({ x: seg.point.x, y: seg.point.y });
    handles.push({
      in: { x: seg.handleIn.x, y: seg.handleIn.y },
      out: { x: seg.handleOut.x, y: seg.handleOut.y },
    });
  }
  const closed = sub.closed === true;
  cp.remove();
  // A seam edited back to straight drops the list entirely rather than carrying
  // an array of zeroes through every commit.
  return { points, handles: normalizeSeamHandles(handles), closed };
}

/** Convenience for the common "give me this seam's real shape" call. */
export function stitchLineSegments(line: StitchLine): {
  segs: SeamSegment[];
  total: number;
} {
  return buildSeamSegments(line.points, line.closed === true, line.handles);
}
